import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '@hokm/shared';
import { PERSIAN_THEME } from '../theme/persianTheme';
import { RENDER_SCALE } from '../renderScale';

interface FloatingCard {
  sprite: Phaser.GameObjects.Image;
  velocity: { x: number; y: number };
  rotationSpeed: number;
  baseScale: number;
  // 3D rotation simulation
  rotationPhase: number;
  rotationSpeed3D: number;
}

interface LayerConfig {
  count: number;
  scale: number;
  alpha: number;
  speedRange: [number, number];
  depth: number;
}

/**
 * Creates cozy ambient animations for the lobby scene.
 * Includes floating cards drifting across the background and gold particle dust.
 */
export class LobbyAmbiance {
  private scene: Phaser.Scene;
  private floatingCards: FloatingCard[] = [];
  private dustEmitter: Phaser.GameObjects.Particles.ParticleEmitter | null = null;
  private active: boolean = true;

  // Card layers: far (slow/small/dim), mid, near (fast/large/bright)
  // Negative depths so cards render behind all UI elements
  private readonly layers: LayerConfig[] = [
    { count: 8, scale: 0.4, alpha: 0.3, speedRange: [8, 12], depth: -3 },
    { count: 10, scale: 0.6, alpha: 0.45, speedRange: [15, 22], depth: -2 },
    { count: 6, scale: 0.8, alpha: 0.6, speedRange: [25, 35], depth: -1 },
  ];

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.create();
  }

  private create(): void {
    this.createParticleTexture();
    this.createFloatingCards();
    this.createGoldDust();
  }

  /**
   * Generate a small circular texture for gold dust particles.
   */
  private createParticleTexture(): void {
    const key = 'gold-dust-particle';
    if (this.scene.textures.exists(key)) return;

    const size = Math.ceil(8 * RENDER_SCALE);
    const graphics = this.scene.make.graphics({});
    graphics.fillStyle(0xffffff);
    graphics.fillCircle(size / 2, size / 2, size / 2);
    graphics.generateTexture(key, size, size);
    graphics.destroy();
  }

  /**
   * Create floating card sprites across all depth layers.
   */
  private createFloatingCards(): void {
    // Ensure card-back texture exists (should be pre-generated by CardRenderer in PreloadScene)
    if (!this.scene.textures.exists('card-back')) {
      console.warn('LobbyAmbiance: card-back texture not found, skipping floating cards');
      return;
    }

    for (const layer of this.layers) {
      for (let i = 0; i < layer.count; i++) {
        const card = this.spawnCard(layer, true);
        this.floatingCards.push(card);
      }
    }
  }

  /**
   * Spawn a floating card from a random screen edge.
   * @param layer - The layer configuration for this card
   * @param initialSpawn - If true, place card randomly on screen; if false, spawn from edge
   */
  private spawnCard(layer: LayerConfig, initialSpawn: boolean = false): FloatingCard {
    const margin = 120;
    let x: number, y: number, vx: number, vy: number;

    const speed = Phaser.Math.FloatBetween(layer.speedRange[0], layer.speedRange[1]);

    if (initialSpawn) {
      // Initial spawn: spread across screen with strong tangential velocity for orbiting effect
      x = Phaser.Math.Between(margin, GAME_WIDTH - margin);
      y = Phaser.Math.Between(margin, GAME_HEIGHT - margin);

      // Give velocity perpendicular to center (tangential) for orbital motion
      const dx = x - GAME_WIDTH / 2;
      const dy = y - GAME_HEIGHT / 2;
      const dist = Math.sqrt(dx * dx + dy * dy) || 1;

      // Tangential velocity (perpendicular to radius) + some randomness
      const tangentX = -dy / dist;
      const tangentY = dx / dist;
      const initialSpeed = speed * 2.5; // Strong initial velocity
      const randomFactor = Phaser.Math.FloatBetween(0.7, 1.3);

      vx = tangentX * initialSpeed * randomFactor + Phaser.Math.FloatBetween(-5, 5);
      vy = tangentY * initialSpeed * randomFactor + Phaser.Math.FloatBetween(-5, 5);
    } else {
      // Respawn: enter from a random edge
      const edge = Phaser.Math.Between(0, 3);

      switch (edge) {
        case 0: // Left edge
          x = -margin;
          y = Phaser.Math.Between(0, GAME_HEIGHT);
          vx = speed;
          vy = Phaser.Math.FloatBetween(-speed * 0.3, speed * 0.3);
          break;
        case 1: // Right edge
          x = GAME_WIDTH + margin;
          y = Phaser.Math.Between(0, GAME_HEIGHT);
          vx = -speed;
          vy = Phaser.Math.FloatBetween(-speed * 0.3, speed * 0.3);
          break;
        case 2: // Top edge
          x = Phaser.Math.Between(0, GAME_WIDTH);
          y = -margin;
          vx = Phaser.Math.FloatBetween(-speed * 0.3, speed * 0.3);
          vy = speed;
          break;
        default: // Bottom edge
          x = Phaser.Math.Between(0, GAME_WIDTH);
          y = GAME_HEIGHT + margin;
          vx = Phaser.Math.FloatBetween(-speed * 0.3, speed * 0.3);
          vy = -speed;
      }
    }

    const baseScale = layer.scale / RENDER_SCALE;
    const sprite = this.scene.add.image(x, y, 'card-back');
    sprite.setScale(baseScale);
    sprite.setAlpha(layer.alpha);
    sprite.setDepth(layer.depth);
    sprite.setRotation(Phaser.Math.FloatBetween(-0.3, 0.3));

    return {
      sprite,
      velocity: { x: vx, y: vy },
      rotationSpeed: Phaser.Math.FloatBetween(-0.05, 0.05),
      baseScale,
      // Random starting phase and slow rotation speed for 3D effect
      rotationPhase: Phaser.Math.FloatBetween(0, Math.PI * 2),
      rotationSpeed3D: Phaser.Math.FloatBetween(0.3, 0.6),
    };
  }

  /**
   * Respawn a card from a screen edge, keeping its layer properties.
   */
  private respawnCard(card: FloatingCard): void {
    const margin = 120;
    const currentAlpha = card.sprite.alpha;

    // Find the layer this card belongs to
    const layer = this.layers.find((l) => Math.abs(l.alpha - currentAlpha) < 0.01);
    if (!layer) return;

    const speed = Phaser.Math.FloatBetween(layer.speedRange[0], layer.speedRange[1]);
    const edge = Phaser.Math.Between(0, 3);

    switch (edge) {
      case 0: // Left
        card.sprite.x = -margin;
        card.sprite.y = Phaser.Math.Between(0, GAME_HEIGHT);
        card.velocity.x = speed;
        card.velocity.y = Phaser.Math.FloatBetween(-speed * 0.3, speed * 0.3);
        break;
      case 1: // Right
        card.sprite.x = GAME_WIDTH + margin;
        card.sprite.y = Phaser.Math.Between(0, GAME_HEIGHT);
        card.velocity.x = -speed;
        card.velocity.y = Phaser.Math.FloatBetween(-speed * 0.3, speed * 0.3);
        break;
      case 2: // Top
        card.sprite.x = Phaser.Math.Between(0, GAME_WIDTH);
        card.sprite.y = -margin;
        card.velocity.x = Phaser.Math.FloatBetween(-speed * 0.3, speed * 0.3);
        card.velocity.y = speed;
        break;
      default: // Bottom
        card.sprite.x = Phaser.Math.Between(0, GAME_WIDTH);
        card.sprite.y = GAME_HEIGHT + margin;
        card.velocity.x = Phaser.Math.FloatBetween(-speed * 0.3, speed * 0.3);
        card.velocity.y = -speed;
    }

    card.sprite.setRotation(Phaser.Math.FloatBetween(-0.3, 0.3));
    card.rotationSpeed = Phaser.Math.FloatBetween(-0.05, 0.05);
    card.rotationPhase = Phaser.Math.FloatBetween(0, Math.PI * 2);
    card.rotationSpeed3D = Phaser.Math.FloatBetween(0.3, 0.6);
  }

  /**
   * Check if a sprite is off-screen (with margin).
   */
  private isOffScreen(sprite: Phaser.GameObjects.Image): boolean {
    const margin = 140;
    return (
      sprite.x < -margin || sprite.x > GAME_WIDTH + margin || sprite.y < -margin || sprite.y > GAME_HEIGHT + margin
    );
  }

  /**
   * Create gold particle dust emitter near the title.
   */
  private createGoldDust(): void {
    const key = 'gold-dust-particle';
    if (!this.scene.textures.exists(key)) return;

    // Create emitter with horizontal spread around the title
    this.dustEmitter = this.scene.add.particles(GAME_WIDTH / 2, 70, key, {
      frequency: 180,
      quantity: 1,
      lifespan: { min: 4000, max: 7000 },
      x: { min: -140, max: 140 },
      y: { min: -15, max: 15 },
      speedX: { min: -12, max: 12 },
      speedY: { min: -6, max: -2 },
      gravityY: -3,
      scale: { start: 0.4 / RENDER_SCALE, end: 0.1 / RENDER_SCALE },
      alpha: { start: 0.4, end: 0 },
      tint: [PERSIAN_THEME.colors.gold, 0xffd700, PERSIAN_THEME.colors.bronze],
      blendMode: Phaser.BlendModes.ADD,
    });

    this.dustEmitter.setDepth(-1);
  }

  /**
   * Update floating card positions. Call this in the scene's update method.
   */
  update(time: number, delta: number): void {
    if (!this.active) return;

    const dt = delta / 1000;
    const wobble = Math.sin(time / 2500) * 0.2;

    for (const card of this.floatingCards) {
      // Very gentle pull toward center - but only when outside the form area
      // Form frame is at center (640, 365) with size 430x390
      const formLeft = 425;
      const formRight = 855;
      const formTop = 170;
      const formBottom = 560;

      const isInFormArea =
        card.sprite.x > formLeft && card.sprite.x < formRight && card.sprite.y > formTop && card.sprite.y < formBottom;

      if (!isInFormArea) {
        const centerX = GAME_WIDTH / 2;
        const centerY = GAME_HEIGHT / 2;
        const pullStrength = 0.015;
        card.velocity.x += (centerX - card.sprite.x) * pullStrength * dt;
        card.velocity.y += (centerY - card.sprite.y) * pullStrength * dt;
      }

      // Limit max velocity so cards don't get too fast
      const maxSpeed = 40;
      const currentSpeed = Math.sqrt(card.velocity.x ** 2 + card.velocity.y ** 2);
      if (currentSpeed > maxSpeed) {
        card.velocity.x = (card.velocity.x / currentSpeed) * maxSpeed;
        card.velocity.y = (card.velocity.y / currentSpeed) * maxSpeed;
      }

      card.sprite.x += card.velocity.x * dt;
      card.sprite.y += (card.velocity.y + wobble) * dt;
      card.sprite.rotation += card.rotationSpeed * dt;

      // Subtle 3D rotation effect - oscillate scaleX to simulate Y-axis rotation
      card.rotationPhase += card.rotationSpeed3D * dt;
      const scaleX = card.baseScale * (0.6 + 0.4 * Math.cos(card.rotationPhase));
      card.sprite.setScale(scaleX, card.baseScale);

      if (this.isOffScreen(card.sprite)) {
        this.respawnCard(card);
      }
    }
  }

  /**
   * Pause all ambient animations.
   */
  pause(): void {
    this.active = false;
    this.dustEmitter?.stop();
  }

  /**
   * Resume ambient animations.
   */
  resume(): void {
    this.active = true;
    this.dustEmitter?.start();
  }

  /**
   * Clean up all ambient objects.
   */
  destroy(): void {
    this.active = false;

    for (const card of this.floatingCards) {
      card.sprite.destroy();
    }
    this.floatingCards = [];

    if (this.dustEmitter) {
      this.dustEmitter.destroy();
      this.dustEmitter = null;
    }
  }
}
